import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type MutationOptions,
  type QueryOptions,
} from '@/modules/query/utils';
import type { GroupingFilterExpression, SortOption } from '@@/packages/shared/libs/search/types';
import type { ClientDocument, ClientDocumentDetail, ClientDocumentListResponse } from '../types';
import {
  downloadClientDocumentsAsZip,
  type DownloadableClientDocument,
  type OmittedDocument,
} from '../downloadAsZip';
import { toFilterParams } from './filterParams';
import {
  CLIENT_DOCUMENT_DOWNLOAD_URL_QUERY,
  CLIENT_DOCUMENT_QUERY,
  CLIENT_DOCUMENTS_LIST_QUERY,
} from './queryKeys';

export type ClientDocumentsParams = {
  connectionID: string;
  limit: number;
  offset: number;
  search: string;
  // `field` names a table column, mapped to the column the endpoint orders on
  // by SORT_FIELDS. Ordering is server-side: sorting the rows on screen would
  // only reorder the page they sit on.
  sort: SortOption;
  // Filtering is server-side for the same reason, and additionally so `total`
  // counts what the filter kept rather than the whole connection.
  filter?: GroupingFilterExpression;
};

// The endpoint names its sortable columns as they are in the database, so the
// table's field names are mapped rather than passed through. A name missing
// from here would be silently ignored by the API and leave the list in its
// default order, so the table's sortingKeys and these keys have to agree.
const SORT_FIELDS: Record<string, string> = {
  title: 'title',
  createdAt: 'created_at',
  modifiedAt: 'modified_at',
  expiryDate: 'expiry_date',
};

export function useClientDocuments(
  params: MaybeRefOrGetter<ClientDocumentsParams>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_DOCUMENTS_LIST_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const { connectionID, limit, offset, search, sort, filter } = toValue(queryKey[1]);
        const res = await request<ClientDocumentListResponse>(
          'GET',
          `/connections/${connectionID}/client-documents`,
          undefined,
          {
            limit: `${limit}`,
            offset: `${offset}`,
            search: search || undefined,
            sortBy: SORT_FIELDS[sort.field],
            sortDirection: sort.direction,
            ...toFilterParams(filter),
          },
        );
        return res.data;
      },
    }),
  );

  const documents = computed(() => query.data.value?.items ?? []);
  const total = computed(() => query.data.value?.page.total ?? 0);

  return { ...query, documents, total };
}

// The same list, read a page at a time and accumulated, for surfaces that scroll
// instead of paginating. `offset` is the page param rather than an argument.
export type InfiniteClientDocumentsParams = Omit<ClientDocumentsParams, 'offset'>;

export function useInfiniteClientDocuments(
  params: MaybeRefOrGetter<InfiniteClientDocumentsParams>,
  options?: QueryOptions,
) {
  const query = useInfiniteQuery({
    ...options,
    // Nested under the list key so the write hooks below, which invalidate on
    // that key alone, reach these pages too.
    queryKey: [CLIENT_DOCUMENTS_LIST_QUERY, 'infinite', toRef(params)] as const,
    async queryFn({ queryKey, pageParam }) {
      const { connectionID, limit, search, sort, filter } = toValue(queryKey[2]);
      const res = await request<ClientDocumentListResponse>(
        'GET',
        `/connections/${connectionID}/client-documents`,
        undefined,
        {
          limit: `${limit}`,
          offset: `${pageParam}`,
          search: search || undefined,
          sortBy: SORT_FIELDS[sort.field],
          sortDirection: sort.direction,
          ...toFilterParams(filter),
        },
      );
      return res.data;
    },
    initialPageParam: 0,
    // Counted from what arrived rather than the requested limit, so a short page
    // ends the list and an empty one can't ask for itself again forever.
    getNextPageParam: (lastPage, _pages, lastOffset) => {
      const next = lastOffset + lastPage.items.length;
      return lastPage.items.length && next < lastPage.page.total ? next : null;
    },
    // mergeQueryOptions is typed for plain queries, so the caller's `enabled` is
    // folded in by hand, as useAvailableOrgs does.
    enabled: () => toValue(options?.enabled) !== false,
    // Keep the rows already on screen while a new search term refetches, so
    // search-as-you-type doesn't flash through spinner and empty per keystroke.
    placeholderData: keepPreviousData,
  });

  const documents = computed(() => query.data.value?.pages.flatMap((page) => page.items) ?? []);
  const total = computed(() => query.data.value?.pages[0]?.page.total ?? 0);

  return { ...query, documents, total };
}

export async function fetchClientDocument(id: string) {
  const res = await request<ClientDocumentDetail>('GET', `/client-documents/${id}`);
  return res.data;
}

export function useClientDocument(id: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_DOCUMENT_QUERY, toRef(id)] as const,
      queryFn: ({ queryKey }) => fetchClientDocument(toValue(queryKey[1])),
    }),
  );

  return { ...query, clientDocument: query.data };
}

export type RecordClientDocumentVariables = {
  connectionID: string;
  title: string;
  expiryDate: string | null;
  uploadID: string;
};

export function useRecordClientDocument(
  options?: MutationOptions<ClientDocument, RecordClientDocumentVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ connectionID, title, expiryDate, uploadID }) {
        const res = await request<ClientDocument>(
          'POST',
          `/connections/${connectionID}/client-documents`,
          { title, expiryDate, uploadID },
        );
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENTS_LIST_QUERY] });
      },
    }),
  );
}

// Both delete endpoints are flat by document id: ids are globally unique and the
// backend authorizes ownership per id, so neither takes a connection.
export function useDeleteClientDocument(options?: MutationOptions<void, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(id) {
        await request('DELETE', `/client-documents/${id}`);
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENTS_LIST_QUERY] });
      },
    }),
  );
}

export function useBulkDeleteClientDocuments(options?: MutationOptions<void, string[]>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(ids) {
        await request('DELETE', '/client-documents', undefined, { ids });
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENTS_LIST_QUERY] });
      },
    }),
  );
}

export type ReviseClientDocumentVariables = {
  id: string;
  title: string;
  expiryDate: string | null;
};

// PATCH takes the full metadata, so a rename has to resend the expiry it is not
// changing, and vice versa.
export function useReviseClientDocument(
  options?: MutationOptions<void, ReviseClientDocumentVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ id, title, expiryDate }) {
        await request('PATCH', `/client-documents/${id}`, { title, expiryDate });
      },
      async onSuccess() {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENTS_LIST_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENT_QUERY] }),
        ]);
      },
    }),
  );
}

export type ReplaceClientDocumentFileVariables = {
  id: string;
  uploadID: string;
};

export function useReplaceClientDocumentFile(
  options?: MutationOptions<void, ReplaceClientDocumentFileVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ id, uploadID }) {
        await request('POST', `/client-documents/${id}/file`, { uploadID });
      },
      async onSuccess() {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENTS_LIST_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENT_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DOCUMENT_DOWNLOAD_URL_QUERY] }),
        ]);
      },
    }),
  );
}

type ClientDocumentDownloadUnavailableReason = 'scanning' | 'scan_rejected' | 'file_missing';

// Hand-mirrors the Go DTO in services/monolith/pkg/api/client_documents.go.
// Nothing generates types across the repo boundary, so a response shape change
// there only reaches us if someone edits this.
export type ClientDocumentDownload = {
  documentID: string;
  filename: string;
  contentType: string | null;
  fileSize: number | null;
  modifiedAt: string;
  // Absent when the document's file cannot be downloaded.
  url?: string;
  // Why the document has no url. Absent when `url` is set.
  unavailable?: ClientDocumentDownloadUnavailableReason;
};

// A plain request rather than a query composable: the URLs are single-use and
// expire after five minutes, so there is nothing worth caching, and the zip calls
// this once per chunk as it drains.
const fetchClientDocumentDownloadURLs = (ids: string[]) =>
  request<{ documents: ClientDocumentDownload[] }>(
    'GET',
    '/client-documents/download-urls',
    undefined,
    { ids },
  );

export type BulkDownloadVariables = {
  documents: DownloadableClientDocument[];
  archiveName: string;
};

// A mutation rather than a query: this is once-only export data with nothing
// worth caching, and `isPending` is the flag the table's button needs while the
// archive builds. See docs/style-guide/data-fetching-strategy.md.
export function useBulkDownloadClientDocuments(
  options?: MutationOptions<OmittedDocument[], BulkDownloadVariables>,
) {
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: ({ documents, archiveName }: BulkDownloadVariables) =>
        downloadClientDocumentsAsZip(documents, archiveName, fetchClientDocumentDownloadURLs),
    }),
  );
}

type ClientDocumentDownloadURL = { url: string };

// Omitting `version` downloads the current file; otherwise it names one of this
// document's history rows.
export const fetchClientDocumentDownloadURL = (id: string, version?: string) =>
  request<ClientDocumentDownloadURL>('GET', `/client-documents/${id}/download`, undefined, {
    version,
  });

// Mirrors modules/uploads/api's useDownloadUrl, but resolves through the
// client-documents endpoint so no upload id enters this path. Only the current
// file is addressable: the thumbnail never shows a superseded version, and
// version downloads go through useDownloadClientDocument instead.
export function useClientDocumentDownloadURL(id: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_DOCUMENT_DOWNLOAD_URL_QUERY, toRef(id)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchClientDocumentDownloadURL(toValue(queryKey[1]));
        return res.data.url;
      },
      // Signed download link expires after 5 minutes
      staleTime: 4.5 * 60e3,
      placeholderData: undefined,

      // Refetching is generally bad as it causes a document to be redownloaded
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,

      // The only consumer is a decorative thumbnail, and the endpoint 403s while
      // a file is still being scanned. Failures must fall back to the icon box,
      // so don't retry, and don't let a 5xx reach the global error boundary
      // (see plugins/vueQuery.ts) where it would take the whole drawer down.
      retry: false,
      throwOnError: false,
    }),
  );

  return { ...query, url: query.data };
}
